/**
 * Capability Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive page for displaying individual capability information with:
 * - Dynamic capability fetching by ID from URL parameters
 * - Dynamic media gallery integration powered by MediaCentre data
 * - Multi-media support (video, audio, images) with streaming capabilities
 * - Series-based media organization and display
 * - Interactive media selection with modal support
 * - HTML content parsing for rich text display
 * - Smooth thumbnail scrolling with navigation controls
 * - Back navigation functionality
 * - Loading states and error handling
 * - Responsive design for various screen sizes
 * 
 * Features:
 * - At a Glance information display
 * - Ireland's Position & History section
 * - Facts & Figures visualization
 * - Operating Environment details
 * - Innovation insights
 * - Dynamic series-based media galleries from MediaCentre
 * 
 * @returns {JSX.Element} Capability page component with dynamic media galleries
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAudio } from '../contexts/AudioContext';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { FILE_NAMES } from '../utils/constants';
import './CapabilityInnovation.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Parser from 'html-react-parser';

/**
 * Capability component for displaying detailed capability information
 * with integrated dynamic media galleries powered by MediaCentre data
 */
function Capability() {

  const { id } = useParams();
  const navigate = useNavigate();
  const [currentCapability, setCurrentCapability] = useState(null);
  const [activeVideoIndices, setActiveVideoIndices] = useState({});
  
  // MediaCentre integration states
  const [allMediaData, setAllMediaData] = useState([]);
  const [seriesGalleries, setSeriesGalleries] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [areThumbnailsLoading, setAreThumbnailsLoading] = useState(true);
  const [autoPlayStates, setAutoPlayStates] = useState({}); // Track which videos should auto-play
  const [isMediaLoading, setIsMediaLoading] = useState(true); // New state for MediaCentre loading
  const [error, setError] = useState(null); // New state for error handling
  
  // Modal states for different media types
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Video loading states
  const [videoSource, setVideoSource] = useState(null);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isVideoEmbedded, setIsVideoEmbedded] = useState(false);
  const currentBlobUrl = useRef(null);
  const loadingRef = useRef(false);

  // Audio context integration
  const audioContext = useAudio();
  const { playAudio } = audioContext;

  // Load capability data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const capabilitiesDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.CAPABILITIES);
        const item = capabilitiesDataResult.fileContent.find(
          item => item.id === parseInt(id)
        );
        if (item) {
          setCurrentCapability(item);
          }
      } catch (err) {
        console.error('Error fetching capability data:', err);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const loadMediaCentreData = async () => {
      try {
        setIsMediaLoading(true);
        
        const CACHE_KEYS = {
          SERIES_GALLERIES: 'capability_series_galleries',
          THUMBNAILS: 'capability_thumbnails',
          IMAGES: 'capability_images',
          CACHE_TIMESTAMP: 'capability_cache_timestamp'
        };
        
        const CACHE_EXPIRY = 30 * 60 * 1000;
        const cacheTimestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
        const now = Date.now();
        const isCacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp)) < CACHE_EXPIRY;
        
        if (isCacheValid) {
          const cachedGalleries = localStorage.getItem(CACHE_KEYS.SERIES_GALLERIES);
          const cachedThumbnails = localStorage.getItem(CACHE_KEYS.THUMBNAILS);
          const cachedImages = localStorage.getItem(CACHE_KEYS.IMAGES);
          
          if (cachedGalleries) {
            const galleries = JSON.parse(cachedGalleries);
            setSeriesGalleries(galleries);
          }
          
          if (cachedThumbnails) {
            const thumbnails = JSON.parse(cachedThumbnails);
            setVideoThumbnails(thumbnails);
          }
          
          if (cachedImages) {
            const images = JSON.parse(cachedImages);
            setImageUrls(images);
          }
          
          if (cachedGalleries && cachedThumbnails && cachedImages) {
            setIsMediaLoading(false);
            return;
          }
        }
        
        const mediaCentreResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEDIA_CENTRE);
        const rawData = mediaCentreResult.fileContent;
        
        const enhancedData = rawData.map((item) => {
          let seriesArray = [];
          
          if (Array.isArray(item.series)) {
            seriesArray = item.series.flatMap(s => 
              typeof s === 'string' ? s.split(',').map(part => part.trim()) : [s]
            );
          } else if (typeof item.series === 'string') {
            seriesArray = item.series.split(',').map(s => s.trim());
          } else if (item.series) {
            seriesArray = [item.series];
          }
          
          const uniqueSeries = [...new Set(seriesArray.filter(s => s && s.trim() !== ''))];
          
          return {
            ...item,
            series: uniqueSeries,
            mediaType: item.mediaType || 'Video'
          };
        });
        const seriesMap = {};
        enhancedData.forEach(item => {
          if (item.series && item.series.length > 0) {
            item.series.forEach(seriesName => {
              if (!seriesMap[seriesName]) {
                seriesMap[seriesName] = {
                  id: seriesName.replace(/\s+/g, '-').toLowerCase(),
                  title: seriesName,
                  videos: []
                };
              }
              seriesMap[seriesName].videos.push({
                id: item.id,
                title: item.topic,
                url: item.webURLLink,
                thumb: item.thumbnail || null,
                duration: item.duration,
                publishDate: item.publishDate,
                entity: item.entity,
                mediaType: item.mediaType,
                shortDescription: item.shortDescription,
                originalItem: item
              });
            });
          }
        });
        
        const galleries = Object.keys(seriesMap)
          .sort()
          .map(seriesName => ({
            ...seriesMap[seriesName],
            videos: seriesMap[seriesName].videos
              .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
          }));
        
        setSeriesGalleries(galleries);
        
        localStorage.setItem(CACHE_KEYS.SERIES_GALLERIES, JSON.stringify(galleries));
        localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, now.toString());
        
        await Promise.all([
          loadImagesFromBlob(galleries),
          fetchThumbnailsForMedia(galleries)
        ]);
        
      } catch (error) {
        console.error('Error loading MediaCentre data:', error);
        setError('Failed to load media galleries');
      } finally {
        setIsMediaLoading(false);
      }
    };
    
    loadMediaCentreData();
  }, []);

  // Initialize activeVideoIndices for each gallery
  useEffect(() => {
    if (seriesGalleries.length > 0) {
      const indices = seriesGalleries.reduce((acc, gallery) => {
        acc[gallery.id] = 0; // Set first video as selected by default
        return acc;
      }, {});
      setActiveVideoIndices(indices);
    }
  }, [seriesGalleries]);

  // Media loading is now handled by the caching system in loadMediaCentreData

  // Video loading logic (adapted from MediaCentre)
  useEffect(() => {
    const loadVideo = async () => {
      if (loadingRef.current || !selectedVideo?.url) return;

      loadingRef.current = true;
      setIsVideoLoading(true);
      setVideoSource(null);
      
      const video = selectedVideo.url;
      
      if (video.includes('https://')) {
        // Check if it's a YouTube or Vimeo URL that needs iframe embedding
        const isYouTube = video.includes('youtube.com') || video.includes('youtu.be');
        const isVimeo = video.includes('vimeo.com');

        if (isYouTube || isVimeo) {
          setVideoSource(getEmbeddableVideoUrl(video));
          setIsVideoEmbedded(true);
        } else {
          setVideoSource(video);
          setIsVideoEmbedded(false);
        }

        setIsVideoLoading(false);
        loadingRef.current = false;
      } else {
        // For local videos, use streaming endpoint
        const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
        const encodedVideo = encodeURIComponent(video);
        const streamingUrl = `${baseUrl}media/stream/${encodedVideo}`;
              
        try {
          const testResponse = await fetch(streamingUrl, { 
            method: 'HEAD'
            // Removed Range headers - HEAD requests are just for checking existence
          });
          
          if (testResponse.ok) {
            setVideoSource(streamingUrl);
            setIsVideoEmbedded(false);
          } else {
            // Fallback to blob method
            const { fileContent } = await apiHelper.fetchVideoByName(video);
            const newBlobUrl = URL.createObjectURL(fileContent);
            currentBlobUrl.current = newBlobUrl;
            setVideoSource(newBlobUrl);
            setIsVideoEmbedded(false);
          }
        } catch (error) {
          try {
            const { fileContent } = await apiHelper.fetchVideoByName(video);
            const newBlobUrl = URL.createObjectURL(fileContent);
            currentBlobUrl.current = newBlobUrl;
            setVideoSource(newBlobUrl);
            setIsVideoEmbedded(false);
          } catch (blobError) {
            console.error('Failed to load video:', blobError);
          }
        }
        
        setIsVideoLoading(false);
        loadingRef.current = false;
      }
    };

    if (selectedVideo?.url) {
      loadVideo();
    }

    return () => {
      if (currentBlobUrl.current) {
        URL.revokeObjectURL(currentBlobUrl.current);
        currentBlobUrl.current = null;
      }
      loadingRef.current = false;
    };
  }, [selectedVideo?.url]);

  // Helper function to convert video URLs to embeddable format
  const getEmbeddableVideoUrl = (url) => {
    if (url.includes('youtube.com/watch?v=')) {
      const videoId = url.split('v=')[1].split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1].split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1].split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url;
  };

  const getYouTubeThumbnail = (youtubeUrl) => {
    try {
      const videoId = youtubeUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (!videoId) return null;
      
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    } catch (err) {
      console.error('Error generating YouTube thumbnail:', err);
      return null;
    }
  };

  // Get Vimeo thumbnail
  const getVimeoThumbnail = async (vimeoUrl) => {
    try {
      const videoId = vimeoUrl.match(/vimeo\.com\/(\d+)/)?.[1];
      if (!videoId) return null;
      
      // Try oEmbed API (modern approach)
      try {
        const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}&width=640`;
        const oembedResponse = await fetch(oembedUrl);
        
        if (!oembedResponse.ok) {
          if (oembedResponse.status === 404) {
            console.warn(`Vimeo video ${videoId} not found via oEmbed. Video may have been deleted or made private.`);
          }
          throw new Error(`oEmbed API returned ${oembedResponse.status}`);
        }
        
        const oembedData = await oembedResponse.json();
        
        if (oembedData.thumbnail_url) {
          return oembedData.thumbnail_url;
        }
      } catch (oembedError) {
        // Fallback to direct CDN approach (no API call needed)
        try {
          const cdnUrl = `https://i.vimeocdn.com/video/${videoId}_640x360.jpg`;
          const testResponse = await fetch(cdnUrl, { method: 'HEAD' });
          if (testResponse.ok) {
            return cdnUrl;
          }
        } catch (cdnError) {
          console.warn(`Failed to get Vimeo thumbnail for video ${videoId}:`, oembedError.message);
        }
      }
      
      return null;
    } catch (err) {
      console.error('Error fetching Vimeo thumbnail:', err);
      return null;
    }
  };

  const loadImagesFromBlob = async (galleries = seriesGalleries) => {
    const CACHE_KEY = 'capability_images';
    
    const cachedImages = localStorage.getItem(CACHE_KEY);
    if (cachedImages) {
      const images = JSON.parse(cachedImages);
      setImageUrls(images);
      return;
    }
    
    const imageUrlsMap = {};
    
    const imageItems = galleries.flatMap(gallery => 
      gallery.videos.filter(video => video.mediaType.toLowerCase() === 'image')
    );
    
    for (const item of imageItems) {
      try {
        if (!item.url.includes('http://') && !item.url.includes('https://')) {
          try {
            // Build proper streaming URL - avoid double /api/ paths
            const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
            const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, ''); // Remove /api from end if present
            const encodedFilename = encodeURIComponent(item.url);
            
            const streamingUrls = [
              `${mediaBaseUrl}/media/stream/${encodedFilename}`,
              `${apiBaseUrl}/media/stream/${encodedFilename}`
            ];
            
            let imageLoaded = false;
            
            for (const streamingUrl of streamingUrls) {
              try {
                const testResponse = await fetch(streamingUrl, { method: 'HEAD' });
                
                if (testResponse.ok) {
                  imageUrlsMap[item.url] = streamingUrl;
                  imageLoaded = true;
                  break;
                }
              } catch (urlError) {
                // Try next URL
              }
            }
            
            if (!imageLoaded) {
              throw new Error('No streaming URLs accessible');
            }
          } catch (error) {
            try {
              const { fileContent } = await apiHelper.fetchImageByName(item.url);
              const imageBlobUrl = URL.createObjectURL(fileContent);
              imageUrlsMap[item.url] = imageBlobUrl;
            } catch (blobError) {
              console.error('Failed to load image:', blobError);
            }
          }
        } else {
          imageUrlsMap[item.url] = item.url;
        }
      } catch (error) {
        console.error('Error processing image:', error);
      }
    }
    
    setImageUrls(imageUrlsMap);
    
    const cacheableImages = Object.fromEntries(
      Object.entries(imageUrlsMap).filter(([_, url]) => !url.startsWith('blob:'))
    );
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheableImages));
  };

  const fetchThumbnailsForMedia = async (galleries = seriesGalleries) => {
    const CACHE_KEY = 'capability_thumbnails';
    
    const cachedThumbnails = localStorage.getItem(CACHE_KEY);
    if (cachedThumbnails) {
      const thumbnails = JSON.parse(cachedThumbnails);
      setVideoThumbnails(thumbnails);
      setAreThumbnailsLoading(false);
      return;
    }
    
    setAreThumbnailsLoading(true);
    
    const mediaThumbnailsMap = {};
    const mediaItems = galleries.flatMap(gallery => 
      gallery.videos.filter(video => video.url)
    );
    
          const batchSize = 5;
      for (let i = 0; i < mediaItems.length; i += batchSize) {
        const batch = mediaItems.slice(i, i + batchSize);
        
        await Promise.all(batch.map(async (item) => {
          try {
            if (item.thumb && item.thumb.trim() !== '' && item.thumb !== 'NONE') {
              if (!item.thumb.includes('http://') && !item.thumb.includes('https://')) {
                try {
                  // Build proper streaming URLs - avoid double /api/ paths
                  const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
                  const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, ''); // Remove /api from end if present
                  const encodedThumbnail = encodeURIComponent(item.thumb);
                  const streamingUrls = [
                    `${mediaBaseUrl}/media/stream/${encodedThumbnail}`,
                    `${apiBaseUrl}/media/stream/${encodedThumbnail}`
                    // Removed /api/media/stream/ - this endpoint doesn't exist
                  ];
                  
                  let thumbnailLoaded = false;
                  
                  for (const streamingUrl of streamingUrls) {
                    try {
                      const controller = new AbortController();
                      const timeoutId = setTimeout(() => controller.abort(), 5000);
                      
                      const testResponse = await fetch(streamingUrl, { 
                        method: 'HEAD',
                        // Removed Range headers - HEAD requests are just for checking existence
                        signal: controller.signal
                      });
                      
                      clearTimeout(timeoutId);
                      
                      if (testResponse.ok) {
                        mediaThumbnailsMap[item.url] = streamingUrl;
                        thumbnailLoaded = true;
                        break;
                      }
                    } catch (streamError) {
                      // Silent failure for streaming attempts
                    }
                  }
                  
                  if (!thumbnailLoaded) {
                    try {
                      const { fileContent } = await apiHelper.fetchImageByName(item.thumb);
                      const thumbnailBlobUrl = URL.createObjectURL(fileContent);
                      mediaThumbnailsMap[item.url] = thumbnailBlobUrl;
                    } catch (blobError) {
                      console.error('Failed to load custom thumbnail blob:', blobError);
                    }
                  }
                } catch (error) {
                  console.error('Error loading custom thumbnail:', error);
                }
              } else {
                mediaThumbnailsMap[item.url] = item.thumb;
              }
            }
            
            const isYouTubeOrVimeo = item.url.includes('vimeo.com') || item.url.includes('youtube.com') || item.url.includes('youtu.be');
            const shouldUsePlatformThumbnail = item.mediaType.toLowerCase() === 'video' && isYouTubeOrVimeo && !mediaThumbnailsMap[item.url];
            
            if (shouldUsePlatformThumbnail) {
              let thumbnailUrl = null;
              
              if (item.url.includes('youtube.com') || item.url.includes('youtu.be')) {
                thumbnailUrl = getYouTubeThumbnail(item.url);
              } else if (item.url.includes('vimeo.com')) {
                try {
                  thumbnailUrl = await getVimeoThumbnail(item.url);
                } catch (vimeoError) {
                  console.error('Error fetching Vimeo thumbnail:', vimeoError);
                }
              }
              
              if (thumbnailUrl) {
                mediaThumbnailsMap[item.url] = thumbnailUrl;
              }
            }
            
          } catch (error) {
            console.error(`Error processing thumbnail for ${item.title}:`, error);
          }
        }));
      }
    
    setVideoThumbnails(mediaThumbnailsMap);
    setAreThumbnailsLoading(false);
    
    const cacheableThumbnails = Object.fromEntries(
      Object.entries(mediaThumbnailsMap).filter(([_, url]) => !url.startsWith('blob:'))
    );
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheableThumbnails));
  };

  const clearMediaCache = () => {
    const cacheKeys = [
      'capability_series_galleries',
      'capability_thumbnails', 
      'capability_images',
      'capability_cache_timestamp'
    ];
    cacheKeys.forEach(key => localStorage.removeItem(key));
  };

  const getCacheInfo = () => {
    const timestamp = localStorage.getItem('capability_cache_timestamp');
    const galleries = localStorage.getItem('capability_series_galleries');
    const thumbnails = localStorage.getItem('capability_thumbnails');
    const images = localStorage.getItem('capability_images');
    
    return {
      timestamp: timestamp ? new Date(parseInt(timestamp)).toLocaleString() : 'No cache',
      galleries: galleries ? JSON.parse(galleries).length : 0,
      thumbnails: thumbnails ? Object.keys(JSON.parse(thumbnails)).length : 0,
      images: images ? Object.keys(JSON.parse(images)).length : 0,
      totalSize: ((galleries?.length || 0) + (thumbnails?.length || 0) + (images?.length || 0)) / 1024 + ' KB'
    };
  };

  useEffect(() => {
    window.clearMediaCache = clearMediaCache;
    window.getCacheInfo = getCacheInfo;
  }, []);

  const handleMediaClick = async (mediaItem, event) => {
    if (mediaItem.mediaType.toLowerCase() === 'image') {
      handleImageClick(mediaItem, event);
      return;
    }
    
    if (mediaItem.mediaType.toLowerCase() === 'audio') {
      if (!playAudio) {
        alert('Audio player is not available. Please refresh the page.');
        return;
      }
      
      try {
        let mousePosition = null;
        if (event) {
          mousePosition = {
            x: event.clientX,
            y: event.clientY,
            scrollY: window.scrollY
          };
        }
        
        // Check if it's an external audio URL
        if (mediaItem.url && (
          mediaItem.url.includes('spotify.com') ||
          mediaItem.url.includes('soundcloud.com') ||
          mediaItem.url.includes('anchor.fm') ||
          mediaItem.url.includes('pod.link') ||
          mediaItem.url.includes('goloudplayer.com') ||
          mediaItem.url.includes('https://') ||
          mediaItem.url.includes('http://')
        )) {
          // Use original item for audio context
          playAudio(mediaItem.originalItem, mousePosition);
          return;
        }
        
        // Handle local audio files
        let audioFilename = mediaItem.url;
        
        if (mediaItem.url.includes('/media/stream/')) {
          audioFilename = mediaItem.url.split('/media/stream/')[1];
        } else if (mediaItem.url.includes('/')) {
          audioFilename = mediaItem.url.split('/').pop();
        }
        
        let audioUrl = null;
        
        try {
          // Build proper streaming URLs - avoid double /api/ paths
          const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, ''); // Remove trailing slashes
          const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, ''); // Remove /api from end if present
          const encodedAudioFilename = encodeURIComponent(audioFilename);
          const streamingUrls = [
            `${mediaBaseUrl}/media/stream/${encodedAudioFilename}`,
            `${apiBaseUrl}/media/stream/${encodedAudioFilename}`
          ];
          
          let audioLoaded = false;
          
          for (const streamingUrl of streamingUrls) {
            try {
              const testResponse = await fetch(streamingUrl, { 
                method: 'HEAD'
                // Removed Range headers - HEAD requests are just for checking existence
              });
              
              if (testResponse.ok) {
                audioUrl = streamingUrl;
                audioLoaded = true;
                break;
              }
            } catch (urlError) {
              // Try next URL
            }
          }
          
          if (!audioLoaded) {
            throw new Error('No streaming URLs accessible');
          }
        } catch (streamError) {
          try {
            const { fileContent } = await apiHelper.fetchAudioByName(audioFilename);
            audioUrl = URL.createObjectURL(fileContent);
          } catch (blobError) {
            alert('Failed to load audio file. Please try again.');
            return;
          }
        }
        
        const audioMediaItem = {
          ...mediaItem.originalItem,
          webURLLink: audioUrl,
          originalWebURLLink: mediaItem.url,
          isLocalBlob: !audioUrl.startsWith('http')
        };
        
        playAudio(audioMediaItem, mousePosition);
        
      } catch (error) {
        alert('Error starting audio: ' + error.message);
      }
      return;
    }
    
    // Handle video files
    if (mediaItem.mediaType.toLowerCase() === 'video') {
      let mousePosition = null;
      if (event) {
        mousePosition = {
          x: event.clientX,
          y: event.clientY,
          scrollY: window.scrollY
        };
      }
      
      setSelectedVideo(mediaItem);
      setShowVideoModal(true);
      
      if (mousePosition) {
        setTimeout(() => {
          const videoModal = document.querySelector('.video-modal');
          const videoOverlay = document.querySelector('.video-modal-overlay');
          
          if (videoModal && videoOverlay) {
            videoOverlay.style.alignItems = 'center';
            videoOverlay.style.justifyContent = 'center';
            videoOverlay.style.padding = '0';
            
            videoModal.style.position = 'fixed';
            videoModal.style.top = '50%';
            videoModal.style.left = '50%';
            videoModal.style.transform = 'translate(-50%, -50%)';
          }
        }, 150);
      }
      return;
    }
    
    // Default fallback
    window.open(mediaItem.url, '_blank');
  };

  // Handle image click
  const handleImageClick = (mediaItem, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setSelectedImage(mediaItem);
    setShowImageModal(true);
  };

  // Close video modal
  const closeVideoModal = () => {
    setShowVideoModal(false);
    setSelectedVideo(null);
    
    setVideoSource(null);
    setIsVideoLoading(true);
    setIsVideoEmbedded(false);
    
    if (currentBlobUrl.current) {
      URL.revokeObjectURL(currentBlobUrl.current);
      currentBlobUrl.current = null;
    }
    
    loadingRef.current = false;
  };

  // Toggle fullscreen for video modal
  const toggleVideoFullscreen = () => {
    const videoModal = document.querySelector('.video-modal');
    
    if (!document.fullscreenElement) {
      if (videoModal.requestFullscreen) {
        videoModal.requestFullscreen();
      } else if (videoModal.webkitRequestFullscreen) {
        videoModal.webkitRequestFullscreen();
      } else if (videoModal.msRequestFullscreen) {
        videoModal.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  };

  // Get Vimeo embed URL
  const getVimeoEmbedUrl = (vimeoUrl) => {
    const match = vimeoUrl.match(/vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/);
    if (!match) return null;
    
    const videoId = match[1];
    const privacyHash = match[2];
    
    let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0`;
    
    if (privacyHash) {
      embedUrl += `&h=${privacyHash}`;
    }
    
    return embedUrl;
  };

  // ESC key handler for modal closing
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showImageModal) {
          setShowImageModal(false);
        }
        if (showVideoModal) {
          closeVideoModal();
        }
      }
    };

    if (showImageModal || showVideoModal) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showImageModal, showVideoModal]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      
      Object.values(videoThumbnails).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleNextVideo = (galleryId, galleryVideos) => {
    setActiveVideoIndices(prev => ({
      ...prev,
      [galleryId]: prev[galleryId] === galleryVideos.length - 1 ? 0 : prev[galleryId] + 1
    }));
  };

  const handlePrevVideo = (galleryId, galleryVideos) => {
    setActiveVideoIndices(prev => ({
      ...prev,
      [galleryId]: prev[galleryId] === 0 ? galleryVideos.length - 1 : prev[galleryId] - 1
    }));
  };

  // Thumbnail scroll handler
  const handleThumbnailScroll = (direction, containerId, e) => {
    if (e) {
    e.preventDefault();
    e.stopPropagation();
    }
    
    const container = document.getElementById(containerId);
    if (!container) return;

    const scrollAmount = 200;
    const currentScroll = container.scrollLeft;
    const newScroll = direction === 'left' ? 
      currentScroll - scrollAmount : 
      currentScroll + scrollAmount;
    
    container.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };



    // Drag functionality for thumbnails
  const setupDragHandlers = (containerId) => {
    const container = document.getElementById(containerId);
    if (!container) return;

    let isDragging = false;
    let hasDragged = false; // Track if user actually dragged
    let startX = 0;
    let startY = 0;
    let scrollLeft = 0;
    let startTime = 0;
    let lastX = 0;
    let velocity = 0;
    const dragThreshold = 5; // Minimum pixels to consider it a drag

    // Mouse events
    const handleMouseDown = (e) => {
      // Only handle left mouse button
      if (e.button !== 0) return;
      
      isDragging = true;
      hasDragged = false;
      startX = e.pageX - container.offsetLeft;
      startY = e.pageY - container.offsetTop;
      scrollLeft = container.scrollLeft;
      startTime = Date.now();
      lastX = startX;
      velocity = 0;
      container.style.cursor = 'grabbing';
      container.style.userSelect = 'none';
      
      // Don't prevent default yet - wait to see if it's actually a drag
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      
      const currentX = e.pageX - container.offsetLeft;
      const currentY = e.pageY - container.offsetTop;
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);
      
      // Only start dragging if moved beyond threshold
      if (!hasDragged && (deltaX > dragThreshold || deltaY > dragThreshold)) {
        hasDragged = true;
        container.classList.add('dragging');
        e.preventDefault();
      }
      
      if (hasDragged) {
        e.preventDefault();
        
        const walk = (currentX - startX) * 1.5;
        const currentTime = Date.now();
        
        velocity = (currentX - lastX) / (currentTime - startTime) * 16;
        lastX = currentX;
        startTime = currentTime;
        
        container.scrollLeft = scrollLeft - walk;
      }
    };

    const handleMouseUp = (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      isDragging = false;
      container.style.cursor = 'grab';
      container.style.userSelect = 'auto';
      container.classList.remove('dragging');
      
      if (hasDragged && Math.abs(velocity) > 1) {
        const momentum = velocity * 10;
        container.classList.add('momentum-scrolling');
    container.scrollTo({
          left: container.scrollLeft - momentum,
        behavior: 'smooth'
    });
        
        // Remove momentum class after animation
        setTimeout(() => {
          container.classList.remove('momentum-scrolling');
        }, 600);
      }
      
      hasDragged = false;
    };

    const handleMouseLeave = () => {
      isDragging = false;
      hasDragged = false;
      container.style.cursor = 'grab';
      container.style.userSelect = 'auto';
      container.classList.remove('dragging');
    };

    // Touch events
    const handleTouchStart = (e) => {
      isDragging = true;
      hasDragged = false;
      const touch = e.touches[0];
      startX = touch.pageX - container.offsetLeft;
      startY = touch.pageY - container.offsetTop;
      scrollLeft = container.scrollLeft;
      startTime = Date.now();
      lastX = startX;
      velocity = 0;
      
      // Don't prevent default yet for touch - wait to see if it's actually a drag
    };

    const handleTouchMove = (e) => {
      if (!isDragging) return;
      
      const touch = e.touches[0];
      const currentX = touch.pageX - container.offsetLeft;
      const currentY = touch.pageY - container.offsetTop;
      const deltaX = Math.abs(currentX - startX);
      const deltaY = Math.abs(currentY - startY);
      
      // Only start dragging if moved beyond threshold
      if (!hasDragged && (deltaX > dragThreshold || deltaY > dragThreshold)) {
        hasDragged = true;
        container.classList.add('dragging');
        e.preventDefault();
      }
      
      if (hasDragged) {
        e.preventDefault();
        
        const walk = (currentX - startX) * 1.5;
        const currentTime = Date.now();
        
        velocity = (currentX - lastX) / (currentTime - startTime) * 16;
        lastX = currentX;
        startTime = currentTime;
        
        container.scrollLeft = scrollLeft - walk;
      }
    };

    const handleTouchEnd = (e) => {
      if (hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      isDragging = false;
      container.classList.remove('dragging');
      
      if (hasDragged && Math.abs(velocity) > 1) {
        const momentum = velocity * 10;
        container.classList.add('momentum-scrolling');
        container.scrollTo({
          left: container.scrollLeft - momentum,
          behavior: 'smooth'
        });
        
        // Remove momentum class after animation
        setTimeout(() => {
          container.classList.remove('momentum-scrolling');
        }, 600);
      }
      
      hasDragged = false;
    };

    // Clean up existing listeners
    container.removeEventListener('mousedown', handleMouseDown);
    container.removeEventListener('mousemove', handleMouseMove);
    container.removeEventListener('mouseup', handleMouseUp);
    container.removeEventListener('mouseleave', handleMouseLeave);
    container.removeEventListener('touchstart', handleTouchStart);
    container.removeEventListener('touchmove', handleTouchMove);
    container.removeEventListener('touchend', handleTouchEnd);

    // Add event listeners
    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    // Set initial cursor
    container.style.cursor = 'grab';
  };

  // Setup drag handlers after galleries are loaded
  useEffect(() => {
    if (seriesGalleries.length > 0) {
      setTimeout(() => {
        seriesGalleries.forEach(gallery => {
          setupDragHandlers(`thumbnails-${gallery.id}`);
        });
      }, 100);
    }
  }, [seriesGalleries, activeVideoIndices]);

  // Get media type icon
  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType.toLowerCase()) {
      case 'video': return 'fas fa-play';
      case 'audio': return 'fas fa-headphones';
      case 'image': return 'fas fa-image';
      default: return 'fas fa-file';
    }
  };

  // Get media type display name
  const getMediaTypeDisplay = (mediaType) => {
    switch (mediaType.toLowerCase()) {
      case 'video': return 'Video';
      case 'audio': return 'Audio';
      case 'image': return 'Image';
      default: return 'Media';
    }
  };

  // Check if media type should show duration
  const showDuration = (mediaType) => {
    return mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio';
  };

  // Check if media type should show play overlay
  const showPlayOverlay = (mediaType) => {
    return mediaType.toLowerCase() === 'video' || mediaType.toLowerCase() === 'audio';
  };

  // Render auto-playing video preview
  const renderVideoPreview = (videoItem, galleryId, videoIndex) => {
    if (!videoItem) return null;

    const videoKey = `${galleryId}-${videoItem.id}`;
    const shouldAutoPlay = autoPlayStates[`${galleryId}-${videoIndex}`];
    
    // If not set to auto-play, show thumbnail
    if (!shouldAutoPlay) {
      return (
        <>
          {videoThumbnails[videoItem.url] ? (
            <img 
              src={videoThumbnails[videoItem.url]} 
              alt={videoItem.title}
              className="video-preview-thumb"
            />
          ) : (
            <div className="video-placeholder">
              <i className="fas fa-play-circle"></i>
              <span>Video</span>
            </div>
          )}
        </>
      );
    }


    
    // Handle YouTube/Vimeo videos
    if (videoItem.url.includes('youtube.com') || videoItem.url.includes('youtu.be')) {
      const videoId = videoItem.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
      if (videoId) {
        return (
          <iframe
            key={videoKey}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&loop=1&playlist=${videoId}`}
            className="video-preview-player"
            allow="autoplay; encrypted-media"
            title={videoItem.title}
          />
        );
      }
    }
    
    if (videoItem.url.includes('vimeo.com')) {
      const videoId = videoItem.url.match(/vimeo\.com\/(\d+)/)?.[1];
      if (videoId) {
        return (
          <iframe
            key={videoKey}
            src={`https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&controls=0&loop=1&title=0&byline=0&portrait=0`}
            className="video-preview-player"
            allow="autoplay; encrypted-media"
            title={videoItem.title}
          />
        );
      }
    }
    
    // Handle local videos
    if (!videoItem.url.includes('http')) {
      const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
      const encodedVideo = encodeURIComponent(videoItem.url);
      const streamingUrl = `${baseUrl}media/stream/${encodedVideo}`;
      
      return (
        <>
          <video
            key={videoKey}
            className="video-preview-player"
            autoPlay
            loop
            playsInline
            src={streamingUrl}
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.parentNode.querySelector('.video-fallback');
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <div className="video-fallback" style={{display: 'none'}}>
            {videoThumbnails[videoItem.url] ? (
              <img 
                src={videoThumbnails[videoItem.url]} 
                alt={videoItem.title}
                className="video-preview-thumb"
              />
            ) : (
              <div className="video-placeholder">
                <i className="fas fa-play-circle"></i>
                <span>Video</span>
              </div>
            )}
          </div>
        </>
      );
    }
    
    // Fallback to thumbnail for external URLs or unknown formats
    return (
      <>
        {videoThumbnails[videoItem.url] ? (
          <img 
            src={videoThumbnails[videoItem.url]} 
            alt={videoItem.title}
            className="video-preview-thumb"
          />
        ) : (
          <div className="video-placeholder">
            <i className="fas fa-play-circle"></i>
            <span>Video</span>
          </div>
        )}
      </>
    );
  };

  // Audio preview with auto-play functionality
  const renderAudioPreview = (audioItem, galleryId, audioIndex) => {
    if (!audioItem) return null;

    const audioKey = `${galleryId}-${audioItem.id}`;
    const shouldAutoPlay = autoPlayStates[`${galleryId}-${audioIndex}`];
    
    // If not set to auto-play, show thumbnail
    if (!shouldAutoPlay) {
      return (
        <>
          {videoThumbnails[audioItem.url] ? (
            <img 
              src={videoThumbnails[audioItem.url]} 
              alt={audioItem.title}
              className="audio-preview-thumb"
            />
          ) : (
            <div className="audio-placeholder">
              <i className="fas fa-headphones"></i>
              <span>Audio</span>
            </div>
          )}
        </>
      );
    }


    
    // Handle Spotify embeds
    if (audioItem.url.includes('spotify.com')) {
      const spotifyId = audioItem.url.match(/spotify\.com\/episode\/([a-zA-Z0-9]+)/)?.[1] || 
                       audioItem.url.match(/spotify\.com\/track\/([a-zA-Z0-9]+)/)?.[1];
      if (spotifyId) {
        return (
          <iframe
            key={audioKey}
            src={`https://open.spotify.com/embed/episode/${spotifyId}?utm_source=generator&theme=0`}
            className="audio-preview-player"
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            title={audioItem.title}
            style={{borderRadius: '12px', background: '#000'}}
          />
        );
      }
    }
    
    // Handle SoundCloud embeds
    if (audioItem.url.includes('soundcloud.com')) {
      return (
        <iframe
          key={audioKey}
          src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(audioItem.url)}&color=%23ff5500&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
          className="audio-preview-player"
          allow="autoplay"
          title={audioItem.title}
        />
      );
    }
    
    // Handle local audio files
    if (!audioItem.url.includes('http') || audioItem.url.includes(process.env.REACT_APP_API_URL)) {
      const baseUrl = process.env.REACT_APP_API_URL.replace('/api/', '/');
      const encodedAudio = encodeURIComponent(audioItem.url.includes('/media/stream/') ? 
        audioItem.url.split('/media/stream/')[1] : audioItem.url);
      const streamingUrl = `${baseUrl}media/stream/${encodedAudio}`;
      
      return (
        <>
          <audio
            key={audioKey}
            className="audio-preview-player"
            autoPlay
            controls
            loop
            src={streamingUrl}
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = e.target.parentNode.querySelector('.audio-fallback');
              if (fallback) fallback.style.display = 'flex';
            }}
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              borderRadius: '8px'
            }}
          />
          <div className="audio-fallback" style={{display: 'none'}}>
            {videoThumbnails[audioItem.url] ? (
              <img 
                src={videoThumbnails[audioItem.url]} 
                alt={audioItem.title}
                className="audio-preview-thumb"
              />
            ) : (
              <div className="audio-placeholder">
                <i className="fas fa-headphones"></i>
                <span>Audio</span>
              </div>
            )}
          </div>
        </>
      );
    }
    
    // Fallback to thumbnail for external URLs or unknown formats
    return (
      <>
        {videoThumbnails[audioItem.url] ? (
          <img 
            src={videoThumbnails[audioItem.url]} 
            alt={audioItem.title}
            className="audio-preview-thumb"
          />
        ) : (
          <div className="audio-placeholder">
            <i className="fas fa-headphones"></i>
            <span>Audio</span>
          </div>
        )}
      </>
    );
  };

  // Dynamic media galleries section
  const mediaGalleriesSection = (
    <div className="video-galleries-sidebar">
      {seriesGalleries.map(gallery => (
        <div key={gallery.id} className="video-gallery-section">
          <div className="gallery-header">
            <Tippy content={gallery.title}>
              <h3 className="gallery-title">{gallery.title}</h3>
            </Tippy>
      
            <Tippy content="View in Media Centre">
                <button
                  className="gallery-expand-button"
                onClick={() => navigate(`/MediaCentre/series/${encodeURIComponent(gallery.title)}`)}
                >
                <i className="fas fa-external-link-alt"></i>
                </button>
              </Tippy>
          </div>
          
          <div className="capability-video-player-container">
            {/* Current media display */}
            <div className="current-media-display" onClick={(e) => {
              const currentVideo = gallery.videos[activeVideoIndices[gallery.id] || 0];
              const isAutoPlaying = autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`];
              
              // If not auto-playing, start auto-play instead of opening modal
              if (!isAutoPlaying) {
                e.preventDefault();
                e.stopPropagation();
                
                setAutoPlayStates(prev => ({
                  ...prev,
                  [`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`]: true
                }));
                             } else {
                 // If already auto-playing, stop auto-play and open modal for full view
                 setAutoPlayStates(prev => ({
                   ...prev,
                   [`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`]: false
                 }));
                 handleMediaClick(currentVideo, e);
               }
            }}>
              {gallery.videos[activeVideoIndices[gallery.id] || 0]?.mediaType.toLowerCase() === 'video' ? (
                // Video display with auto-play
                <div className={`video-thumbnail-display ${autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] ? 'auto-playing' : ''}`}>
                  {renderVideoPreview(gallery.videos[activeVideoIndices[gallery.id] || 0], gallery.id, activeVideoIndices[gallery.id] || 0)}
                  <div className="play-overlay-large">
                    <i className={autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] ? "fas fa-expand" : "fas fa-play"}></i>
                  </div>
                  {/* Pause/Stop controls for auto-playing videos */}
                  {autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] && (
                    <div className="preview-controls">
                      <button 
                        className="preview-pause-btn" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          setAutoPlayStates(prev => ({
                            ...prev,
                            [`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`]: false
                          }));
                        }}
                        title="Stop auto-play"
                      >
                        <i className="fas fa-pause"></i>
                      </button>
                    </div>
                  )}
                  {gallery.videos[activeVideoIndices[gallery.id] || 0]?.duration && (
                    <div className="duration-badge-large">{gallery.videos[activeVideoIndices[gallery.id] || 0]?.duration}</div>
                  )}
                </div>
              ) : gallery.videos[activeVideoIndices[gallery.id] || 0]?.mediaType.toLowerCase() === 'audio' ? (
                // Audio display with auto-play
                <div className={`audio-thumbnail-display ${autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] ? 'auto-playing' : ''}`}>
                  {renderAudioPreview(gallery.videos[activeVideoIndices[gallery.id] || 0], gallery.id, activeVideoIndices[gallery.id] || 0)}
                  <div className="play-overlay-large">
                    <i className={autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] ? "fas fa-volume-up" : "fas fa-play"}></i>
                  </div>
                  {/* Pause/Stop controls for auto-playing audio */}
                  {autoPlayStates[`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`] && (
                    <div className="preview-controls">
                      <button 
                        className="preview-pause-btn" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          
                          setAutoPlayStates(prev => ({
                            ...prev,
                            [`${gallery.id}-${activeVideoIndices[gallery.id] || 0}`]: false
                          }));
                        }}
                        title="Stop auto-play"
                      >
                        <i className="fas fa-pause"></i>
                      </button>
                    </div>
                  )}
                  {gallery.videos[activeVideoIndices[gallery.id] || 0]?.duration && (
                    <div className="duration-badge-large">{gallery.videos[activeVideoIndices[gallery.id] || 0]?.duration}</div>
                  )}
                </div>
              ) : (
                // Image display
                <div className="image-thumbnail-display">
                  {imageUrls[gallery.videos[activeVideoIndices[gallery.id] || 0]?.url] ? (
                    <img 
                      src={imageUrls[gallery.videos[activeVideoIndices[gallery.id] || 0]?.url]} 
                      alt={gallery.videos[activeVideoIndices[gallery.id] || 0]?.title}
                      className="image-preview-thumb"
                    />
                  ) : (
                    <div className="image-placeholder">
                      <i className="fas fa-image"></i>
                      <span>Image</span>
                    </div>
                  )}
                  <div className="image-overlay-large">
                    <i className="fas fa-expand"></i>
                  </div>
                </div>
              )}
            </div>

            {/* Media title */}
            <div className="current-media-title">
              <h4>{gallery.videos[activeVideoIndices[gallery.id] || 0]?.title}</h4>
              <span className="media-type-indicator">
                <i className={getMediaTypeIcon(gallery.videos[activeVideoIndices[gallery.id] || 0]?.mediaType)}></i>
                {getMediaTypeDisplay(gallery.videos[activeVideoIndices[gallery.id] || 0]?.mediaType)}
              </span>
            </div>

            {/* Thumbnails navigation */}
            {gallery.videos.length > 1 && (
                <div className="video-thumbnails-wrapper">
                  <button
                    className="sidebar-nav-button prev"
                    onClick={(e) => handleThumbnailScroll('left', `thumbnails-${gallery.id}`, e)}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>

                  <div id={`thumbnails-${gallery.id}`} className="video-thumbnails">
                    {gallery.videos.map((video, index) => (
                    <Tippy key={video.id} content={`${video.title} (${getMediaTypeDisplay(video.mediaType)})`}>
                        <div
                          className={`thumbnail ${index === activeVideoIndices[gallery.id] ? 'active' : ''}`}
                          onClick={(e) => {
                            // Update active video index and enable auto-play
                            setActiveVideoIndices(prev => ({
                              ...prev,
                              [gallery.id]: index
                            }));
                            setAutoPlayStates(prev => ({
                              ...prev,
                              [`${gallery.id}-${index}`]: true
                            }));
                            
                            // Don't open modal - just play in main display
                          }}
                      >
                        {video.mediaType.toLowerCase() === 'video' ? (
                          videoThumbnails[video.url] ? (
                            <div
                              className="thumbnail-image"
                          style={{
                                backgroundImage: `url(${videoThumbnails[video.url]})`,
                            backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            >
                              <div className="thumbnail-overlay">
                                <i className="fas fa-play"></i>
                              </div>
                            </div>
                          ) : (
                            <div className="thumbnail-placeholder">
                              <i className="fas fa-play"></i>
                            </div>
                          )
                        ) : video.mediaType.toLowerCase() === 'audio' ? (
                          videoThumbnails[video.url] ? (
                            <div
                              className="thumbnail-image"
                              style={{
                                backgroundImage: `url(${videoThumbnails[video.url]})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            >
                              <div className="thumbnail-overlay">
                                <i className="fas fa-headphones"></i>
                              </div>
                            </div>
                          ) : (
                            <div className="thumbnail-placeholder audio">
                              <i className="fas fa-headphones"></i>
                            </div>
                          )
                        ) : (
                          imageUrls[video.url] ? (
                            <div
                              className="thumbnail-image"
                              style={{
                                backgroundImage: `url(${imageUrls[video.url]})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                              }}
                            >
                              <div className="thumbnail-overlay">
                                <i className="fas fa-expand"></i>
                              </div>
                            </div>
                          ) : (
                            <div className="thumbnail-placeholder image">
                              <i className="fas fa-image"></i>
                            </div>
                          )
                        )}
                        
                        <div className="media-type-indicator-thumb">
                          <i className={getMediaTypeIcon(video.mediaType)}></i>
                        </div>
                        </div>
                      </Tippy>
                    ))}
                  </div>

                  <button
                    className="sidebar-nav-button next"
                    onClick={(e) => handleThumbnailScroll('right', `thumbnails-${gallery.id}`, e)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  // Setup drag handlers after galleries are loaded
  useEffect(() => {
    if (seriesGalleries.length > 0) {
      setTimeout(() => {
        seriesGalleries.forEach(gallery => {
          setupDragHandlers(`thumbnails-${gallery.id}`);
        });
      }, 100);
    }
  }, [seriesGalleries, activeVideoIndices]);

  return !currentCapability ? (
    <LoadingSpinner />
  ) : (
    <div className='totalPage'>
      <section className="wrapper">
        <div className="container mt-6">
          <div className="capability-header">
            <h2 className="text-uppercase main-header">{currentCapability.title}</h2>
            <button
              onClick={() => window.history.back()}
              className="btn btn-primary"
            >
              <i className="fas fa-arrow-left"></i> Back
            </button>
          </div>

          <div className="capability-content-layout">
            <div className="main-content">
              <div className="capability-content-section">
                <div className="brochure-content">
                  {currentCapability.atGlance && (
                    <>
                      <h3>At a Glance</h3>
                      <div className="summary">
                        {Parser(currentCapability.atGlance)}
                      </div>
                    </>
                  )}
                  {currentCapability.irelandsPosition && (
                    <>
                      <h3>Ireland's Position & History</h3>
                      <div className="summary">
                        {Parser(currentCapability.irelandsPosition)}
                      </div>
                    </>
                  )}
                  {currentCapability.factsFigures && (
                    <>
                      <h3>Facts & Figures</h3>
                      <div className="summary">
                        {Parser(currentCapability.factsFigures)}
                      </div>
                    </>
                  )}
                  {currentCapability.operatingEnviornment && (
                    <>
                      <h3>Operating Environment</h3>
                      <div className="summary">
                        {Parser(currentCapability.operatingEnviornment)}
                      </div>
                    </>
                  )}
                  {currentCapability.innovation && (
                    <>
                      <h3>Innovation</h3>
                      <div className="summary">
                        {Parser(currentCapability.innovation)}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {isMediaLoading ? (
              <div className="video-galleries-sidebar">
                <div className="loading-galleries">
                  <LoadingSpinner text="Loading Media Galleries..." />
          </div>
        </div>
            ) : error ? (
              <div className="video-galleries-sidebar">
                <div className="loading-galleries">
                  <p style={{color: '#d32f2f', textAlign: 'center'}}>
                    {error}
                    <br />
                    <button 
                      onClick={() => {
                        setError(null);
                        window.clearMediaCache();
                        window.location.reload();
                      }}
                      style={{
                        marginTop: '10px',
                        padding: '8px 16px',
                        backgroundColor: 'var(--ifia-green)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      🔄 Retry
                    </button>
                  </p>
                </div>
              </div>
            ) : seriesGalleries.length > 0 ? mediaGalleriesSection : (
              <div className="video-galleries-sidebar">
                <div className="loading-galleries">
                  <p style={{color: '#666', textAlign: 'center'}}>
                    📱 No media galleries available
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      
      {showVideoModal && selectedVideo && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{selectedVideo.title}</h3>
              <div className="video-modal-controls">
                <button className="fullscreen-btn" onClick={toggleVideoFullscreen} title="Toggle Fullscreen">
                  <i className="fas fa-expand"></i>
                  <i className="fas fa-compress"></i>
                </button>
                <button className="close-btn" onClick={closeVideoModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="video-modal-content">
              {(selectedVideo.url.includes('vimeo.com') || 
                selectedVideo.url.includes('youtube.com') || 
                selectedVideo.url.includes('youtu.be')) ? (
                <iframe
                  src={selectedVideo.url.includes('vimeo.com') 
                    ? getVimeoEmbedUrl(selectedVideo.url)
                    : getEmbeddableVideoUrl(selectedVideo.url)
                  }
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.title}
                ></iframe>
              ) : (
                <>
                  {isVideoLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <LoadingSpinner />
                    </div>
                  ) : videoSource ? (
                    isVideoEmbedded ? (
                      <iframe
                        src={videoSource}
                        width="100%"
                        height="100%"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={selectedVideo.title}
                      />
                    ) : (
                      <video
                        width="100%"
                        height="100%"
                        controls
                        autoPlay
                        src={videoSource}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#666' }}>
                      <p>Failed to load video</p>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="video-modal-info">
              <div className="video-modal-meta media-meta">
                {selectedVideo.duration && (
                  <span><i className="fas fa-clock"></i> {selectedVideo.duration}</span>
                )}
                <span><i className="fas fa-calendar"></i> {new Date(selectedVideo.publishDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}</span>
                <span className='entity'><i className="fas fa-building"></i> {selectedVideo.entity}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showImageModal && selectedImage && (
        <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="image-modal-header">
              <h3>
                <span>{selectedImage.title.length > 50 ? `${selectedImage.title.substring(0, 50)}...` : selectedImage.title}</span>
              </h3>
              <button 
                className="image-modal-close"
                onClick={() => setShowImageModal(false)}
                title="Close image"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="image-modal-content">
              <img src={imageUrls[selectedImage.url] || selectedImage.url} alt={selectedImage.title} />
            </div>
            <div className="image-modal-info">
              <div className="image-modal-meta media-meta">
                <span><i className="fas fa-calendar"></i> {new Date(selectedImage.publishDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}</span>
                <span className='entity'><i className="fas fa-building"></i> {selectedImage.entity}</span>
                {selectedImage.shortDescription && (
                  <div className="image-description">
                    <p>{selectedImage.shortDescription}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Capability;